import languageIndex from "language-subtag-registry/data/json/language.json";
import regionIndex from "language-subtag-registry/data/json/region.json";
import registry from "language-subtag-registry/data/json/registry.json";

const sortByLabel = (a, b) => a.label.localeCompare(b.label);

const browserLocale =
	typeof navigator !== "undefined" && navigator.language ? navigator.language : "en";

const languageDisplayNames =
	typeof Intl !== "undefined" && Intl.DisplayNames
		? new Intl.DisplayNames([browserLocale], {
				type: "language",
				languageDisplay: "standard",
		  })
		: null;

const regionDisplayNames =
	typeof Intl !== "undefined" && Intl.DisplayNames
		? new Intl.DisplayNames([browserLocale], { type: "region" })
		: null;

const buildOption = (code, fallbackLabel, displayNames) => {
	const label = displayNames?.of(code) || fallbackLabel || code;

	return {
		label,
		value: code,
	};
};

// Curated ISO 639-1 language codes — common business/translation targets.
// The registry-driven list pulls in obscure codes (aa, ab, ae...) that
// aren't relevant for model translation. Pinned languages render first,
// the rest are alphabetised by display label.
const PINNED_LANGUAGE_CODES = ["es", "fr"];
const CURATED_LANGUAGE_CODES = [
	"ar", // Arabic
	"bn", // Bengali
	"cs", // Czech
	"da", // Danish
	"de", // German
	"el", // Greek
	"en", // English
	"es", // Spanish
	"fi", // Finnish
	"fr", // French
	"he", // Hebrew
	"hi", // Hindi
	"hu", // Hungarian
	"id", // Indonesian
	"it", // Italian
	"ja", // Japanese
	"ko", // Korean
	"ms", // Malay
	"nl", // Dutch
	"no", // Norwegian
	"pl", // Polish
	"pt", // Portuguese
	"ro", // Romanian
	"ru", // Russian
	"sv", // Swedish
	"th", // Thai
	"tr", // Turkish
	"uk", // Ukrainian
	"vi", // Vietnamese
	"zh", // Chinese
];

const buildLanguageOption = (code) => {
	const recordIndex = languageIndex[code];
	const fallbackLabel = recordIndex != null ? registry[recordIndex]?.Description?.[0] : null;
	return buildOption(code, fallbackLabel, languageDisplayNames);
};

const pinnedLanguages = PINNED_LANGUAGE_CODES.map(buildLanguageOption);
const otherLanguages = CURATED_LANGUAGE_CODES.filter(
	(code) => !PINNED_LANGUAGE_CODES.includes(code)
)
	.map(buildLanguageOption)
	.sort(sortByLabel);

export const languageOptions = [...pinnedLanguages, ...otherLanguages];

// Curated ISO 3166-1 country codes — the registry contains many
// reserved/private codes that don't map to real country names. Pinned
// countries render first, the rest are alphabetised by display label.
const PINNED_COUNTRY_CODES = ["mx", "es"];
const CURATED_COUNTRY_CODES = [
	"ar", // Argentina
	"au", // Australia
	"at", // Austria
	"be", // Belgium
	"br", // Brazil
	"ca", // Canada
	"cl", // Chile
	"cn", // China
	"co", // Colombia
	"cz", // Czechia
	"dk", // Denmark
	"eg", // Egypt
	"fi", // Finland
	"fr", // France
	"de", // Germany
	"gr", // Greece
	"hk", // Hong Kong
	"hu", // Hungary
	"in", // India
	"id", // Indonesia
	"ie", // Ireland
	"il", // Israel
	"it", // Italy
	"jp", // Japan
	"kr", // South Korea
	"my", // Malaysia
	"mx", // Mexico
	"nl", // Netherlands
	"nz", // New Zealand
	"no", // Norway
	"pe", // Peru
	"ph", // Philippines
	"pl", // Poland
	"pt", // Portugal
	"ro", // Romania
	"ru", // Russia
	"sa", // Saudi Arabia
	"sg", // Singapore
	"za", // South Africa
	"es", // Spain
	"se", // Sweden
	"ch", // Switzerland
	"tw", // Taiwan
	"th", // Thailand
	"tr", // Türkiye
	"ua", // Ukraine
	"ae", // United Arab Emirates
	"gb", // United Kingdom
	"us", // United States
	"vn", // Vietnam
];

const buildCountryOption = (code) => {
	const recordIndex = regionIndex[code];
	const fallbackLabel = recordIndex != null ? registry[recordIndex]?.Description?.[0] : null;
	// Intl.DisplayNames for regions is case-sensitive — lowercase codes
	// echo back as the code itself, so we must uppercase here.
	const displayName = regionDisplayNames?.of(code.toUpperCase());
	return {
		label: displayName || fallbackLabel || code,
		value: code,
	};
};

const pinnedCountries = PINNED_COUNTRY_CODES.map(buildCountryOption);
const otherCountries = CURATED_COUNTRY_CODES.filter(
	(code) => !PINNED_COUNTRY_CODES.includes(code)
)
	.map(buildCountryOption)
	.sort(sortByLabel);

export const countryOptions = [...pinnedCountries, ...otherCountries];

// Language → territory pairs derived from the Unicode CLDR
// "Language-Territory Information" supplemental data (chart 49).
// Source: https://www.unicode.org/cldr/charts/49/supplemental/language_territory_information.html
//
// Filters applied:
//   - Skips script variants (e.g. az_Arab, bs_Cyrl) — base codes cover them
//   - Skips entries with population 0.0 / empty / single digits
//   - Skips the "und" (Unknown language) catch-all
//
// Territory codes are ISO 3166-1 alpha-2 lowercase. First entry per language
// is the largest / canonical territory.
const LANGUAGE_TO_COUNTRIES = {
	aa: ["et", "dj", "er"], // Afar
	ab: ["ge", "tr"], // Abkhazian
	af: ["za", "na", "bw"], // Afrikaans
	ak: ["gh"], // Akan
	am: ["et", "il"], // Amharic
	ar: [
		"eg", "sa", "dz", "sd", "iq", "ye", "ma", "sy", "jo", "tn",
		"ae", "ly", "ps", "lb", "so", "mr", "ss", "td", "om", "kw",
		"qa", "il", "ir", "bh", "ca", "eh", "km", "tr", "er", "gb",
		"ml", "ng", "cm", "dj", "ne", "ke", "cy",
	], // Arabic
	as: ["in"], // Assamese
	ay: ["bo", "pe"], // Aymara
	az: ["az", "tr"], // Azeri
	ba: ["ru"], // Bashkir
	be: ["by", "ua", "pl", "ru"], // Belarusian
	bg: ["bg", "tr", "md", "ua", "gr"], // Bulgarian
	bi: ["vu"], // Bislama
	bn: ["bd", "in", "gb", "ca", "sg", "np"], // Bangla
	bo: ["cn", "in", "np"], // Tibetan
	br: ["fr"], // Breton
	bs: ["ba"], // Bosnian
	ca: ["es", "fr", "ad", "it"], // Catalan
	ce: ["ru"], // Chechen
	ch: ["gu", "mp"], // Chamorro
	co: ["fr"], // Corsican
	cs: ["cz", "sk"], // Czech
	cv: ["ru"], // Chuvash
	cy: ["gb", "ar"], // Welsh
	da: ["dk", "de", "gl", "is"], // Danish
	de: [
		"de", "nl", "at", "pl", "ch", "gb", "fr", "dk", "be", "br",
		"hu", "cz", "us", "kz", "sk", "ru", "fi", "it", "si", "bg",
		"gr", "ca", "lu", "lt", "py", "li", "ro", "na",
	], // German
	dv: ["mv"], // Divehi
	dz: ["bt"], // Dzongkha
	ee: ["gh", "tg"], // Ewe
	el: ["gr", "cy", "de", "ca", "eg", "al", "it", "ua", "ro", "tr"], // Greek
	en: [
		"us", "in", "pk", "ng", "ph", "gb", "de", "et", "tz", "eg",
		"ca", "sd", "bd", "fr", "au", "it", "th", "za", "br", "mx",
		"nl", "iq", "tr", "mw", "pl", "cm", "es", "ke", "zw", "be",
		"at", "ro", "ru", "gr", "mg", "ma", "dk", "ie", "nz", "jo",
		"pg", "ae", "sg", "lr", "ch", "fi", "er", "hk", "ss", "zm",
		"dz", "ar", "sl", "kz", "cz", "ye", "jm", "pt", "lk", "lb",
		"rw", "hr", "bw", "hu", "ug", "cl", "ba", "bg", "pr", "sk",
		"tt", "si", "gm", "lt", "cy", "mu", "np", "sz", "fj", "lv",
		"gy", "sb", "pa", "ls", "ee", "gt", "mt", "bs", "lu", "bz",
		"bb", "mv", "vu", "na", "gu", "lc", "mo", "ki", "gd", "je",
		"bt", "vc", "im", "ag", "vi", "mh", "dm", "gg", "bm", "ky",
		"cn", "tc", "fm", "kn", "mp", "as", "hn", "vg", "sc", "sx",
		"to", "gi", "ai", "nr", "do", "bn", "ck", "bi", "sh", "ws",
		"io", "ms", "aw", "fk", "pw", "nf", "cx", "nu", "tv", "tk",
	], // English
	eo: ["sm"], // Esperanto
	es: [
		"mx", "ar", "es", "co", "ph", "us", "ve", "pe", "cl", "ec",
		"gt", "cu", "fr", "hn", "do", "bo", "sv", "gb", "ni", "de",
		"py", "cr", "pa", "uy", "pr", "ic", "ro", "gq", "ca", "pt",
		"bz", "ea", "br", "ad", "ma", "gi", "cw", "sx", "tt",
	], // Spanish
	et: ["ee", "fi"], // Estonian
	eu: ["es", "fr"], // Basque
	fa: ["ir", "af", "pk", "iq", "qa", "ca", "ae", "tj", "om"], // Persian
	ff: [
		"sn", "gn", "cm", "mr", "bf", "gh", "gm", "gw", "lr", "ne",
		"ng", "sl",
	], // Fula
	fi: ["fi", "ee", "se", "ru"], // Finnish
	fj: ["fj"], // Fijian
	fo: ["fo", "dk"], // Faroese
	fr: [
		"fr", "cd", "cm", "dz", "de", "ma", "it", "gb", "ca", "ci",
		"bi", "mg", "tn", "bf", "bj", "ht", "sn", "be", "gn", "ml",
		"cg", "tg", "ch", "ne", "nl", "td", "ro", "lb", "us", "cf",
		"ga", "pt", "sy", "at", "mu", "rw", "gr", "re", "ie", "lu",
		"mr", "gq", "dj", "gp", "mq", "gw", "nc", "pf", "km", "gf",
		"yt", "hu", "vu", "cy", "mt", "sc", "st", "mc", "mf", "wf",
		"bl", "ad", "pm", "tf",
	], // French
	fy: ["nl"], // Western Frisian
	ga: ["ie", "gb"], // Irish
	gd: ["gb"], // Scottish Gaelic
	gl: ["es", "pt"], // Galician
	gn: ["py", "bo", "ar"], // Guarani
	gu: ["in", "gb", "ca", "ke"], // Gujarati
	gv: ["im"], // Manx
	ha: ["ng", "ne", "gh"], // Hausa
	he: ["il"], // Hebrew
	hi: ["in", "za", "ca", "fj", "np", "ug"], // Hindi
	ho: ["pg"], // Hiri Motu
	hr: ["hr", "si", "de", "ba", "at", "ca", "rs", "hu", "it"], // Croatian
	ht: ["ht"], // Haitian Creole
	hu: ["hu", "ro", "sk", "rs", "ua", "il", "ca", "at", "si"], // Hungarian
	hy: ["am", "ru", "sy", "ge", "lb", "ir", "tr", "cy"], // Armenian
	hz: ["na"], // Herero
	id: ["id", "nl", "sg"], // Indonesian
	ig: ["ng"], // Igbo
	ii: ["cn"], // Sichuan Yi
	ik: ["us"], // Inupiaq
	is: ["is"], // Icelandic
	it: [
		"it", "de", "ch", "us", "fr", "at", "br", "ca", "au", "mt",
		"gb", "hr", "sm", "si", "va",
	], // Italian
	iu: ["ca"], // Inuktitut
	ja: ["jp", "br", "ca", "sg"], // Japanese
	jv: ["id", "my", "sg"], // Javanese
	ka: ["ge", "ru", "ir", "tr"], // Georgian
	kg: ["cd"], // Kongo
	ki: ["ke"], // Kikuyu
	kj: ["na"], // Kuanyama
	kk: ["kz", "ru"], // Kazakh
	kl: ["gl", "dk"], // Kalaallisut
	km: ["kh"], // Khmer
	kn: ["in"], // Kannada
	ko: ["kr", "kp", "cn", "us", "jp", "ca", "br"], // Korean
	ks: ["in", "pk"], // Kashmiri
	ku: ["tr", "sy", "de", "am", "az"], // Kurdish
	kv: ["ru"], // Komi
	kw: ["gb"], // Cornish
	ky: ["kg"], // Kyrgyz
	la: ["va"], // Latin
	lb: ["lu"], // Luxembourgish
	lg: ["ug"], // Ganda
	li: ["nl"], // Limburgish
	ln: ["cd", "ao", "cg", "cf"], // Lingala
	lo: ["la"], // Lao
	lt: ["lt", "gb", "pl"], // Lithuanian
	lu: ["cd"], // Luba-Katanga
	lv: ["lv"], // Latvian
	mg: ["mg"], // Malagasy
	mh: ["mh"], // Marshallese
	mi: ["nz"], // Māori
	mk: ["mk", "gr", "al"], // Macedonian
	ml: ["in", "ae", "my", "bh", "sg", "il", "qa"], // Malayalam
	mn: ["mn", "ru"], // Mongolian
	mr: ["in"], // Marathi
	ms: ["my", "id", "sg", "bn"], // Malay
	mt: ["mt"], // Maltese
	my: ["mm", "bd"], // Burmese
	na: ["nr"], // Nauru
	nb: ["no", "sj"], // Norwegian Bokmål
	nd: ["zw"], // North Ndebele
	ne: ["np", "in", "bt"], // Nepali
	ng: ["na"], // Ndonga
	nl: [
		"nl", "de", "be", "sr", "aw", "ca", "fr", "cw", "bq", "sx",
	], // Dutch
	nn: ["no"], // Norwegian Nynorsk
	no: ["no"], // Norwegian
	nr: ["za"], // South Ndebele
	nv: ["us"], // Navajo
	ny: ["mw", "zm", "mz", "zw"], // Nyanja
	oc: ["fr", "es"], // Occitan
	om: ["et", "ke", "so"], // Oromo
	or: ["in"], // Odia
	os: ["ru", "ge"], // Ossetic
	pa: ["in", "gb", "ca", "sg", "ke"], // Punjabi
	pl: ["pl", "gb", "ua", "de", "ca", "il", "cz", "sk", "ro"], // Polish
	ps: ["pk", "af", "ae", "ir"], // Pashto
	pt: [
		"br", "ao", "pt", "mz", "gw", "tl", "fr", "cv", "ca", "ch",
		"st", "gb", "lu", "mo", "ag", "gq",
	], // Portuguese
	qu: ["pe", "bo", "ec"], // Quechua
	rm: ["ch"], // Romansh
	rn: ["bi"], // Rundi
	ro: ["ro", "md", "gb", "il", "ua", "rs", "ca", "hu"], // Romanian
	ru: [
		"ru", "ua", "kz", "pl", "by", "uz", "de", "kg", "lt", "am",
		"bg", "tj", "il", "us", "tm", "lv", "ee", "ge", "ca", "md",
		"fi", "cn", "mn", "sj",
	], // Russian
	rw: ["rw", "ug", "cd"], // Kinyarwanda
	sa: ["in"], // Sanskrit
	sc: ["it"], // Sardinian
	sd: ["pk", "in"], // Sindhi
	se: ["se", "no", "fi"], // Northern Sami
	sg: ["cf"], // Sango
	si: ["lk"], // Sinhala
	sk: ["sk", "cz", "rs", "hu"], // Slovak
	sl: ["si", "it", "at", "hu"], // Slovenian
	sm: ["ws", "as", "tk"], // Samoan
	sn: ["zw"], // Shona
	so: ["so", "et", "ke", "dj", "gb", "ca"], // Somali
	sq: ["al", "xk", "rs", "mk", "me", "tr", "gr"], // Albanian
	sr: ["rs", "ba", "xk", "ca", "me", "ru"], // Serbian
	ss: ["za", "sz", "ls"], // Swati
	st: ["za", "ls"], // Southern Sotho
	su: ["id"], // Sundanese
	sv: ["se", "fi", "dk", "ax"], // Swedish
	sw: ["tz", "cd", "ke", "ug", "so", "mz", "bi", "yt", "za"], // Swahili
	ta: ["in", "lk", "gb", "my", "sg", "ca", "re", "mu"], // Tamil
	te: ["in"], // Telugu
	tg: ["tj", "ru"], // Tajik
	th: ["th", "sg"], // Thai
	ti: ["et", "er", "il"], // Tigrinya
	tk: ["tm", "ir", "af"], // Turkmen
	tn: ["za", "bw", "zw", "na"], // Tswana
	to: ["to"], // Tongan
	tr: [
		"tr", "de", "bg", "cy", "uz", "nl", "ua", "gb", "gr", "ca",
		"mk", "ro",
	], // Turkish
	ts: ["za", "mz", "sz"], // Tsonga
	tt: ["ru"], // Tatar
	ty: ["pf"], // Tahitian
	ug: ["cn", "af"], // Uyghur
	uk: ["ua", "ru", "md", "pl", "ca", "sk", "rs"], // Ukrainian
	ur: ["pk", "in", "gb", "ca", "mu"], // Urdu
	uz: ["uz", "tm", "ru", "tr"], // Uzbek
	ve: ["za", "zw"], // Venda
	vi: ["vn", "us", "ca", "cn"], // Vietnamese
	wa: ["be"], // Walloon
	wo: ["sn", "mr"], // Wolof
	xh: ["za", "ls"], // Xhosa
	yi: ["ua", "il", "us", "se"], // Yiddish
	yo: ["ng", "bj"], // Yoruba
	za: ["cn"], // Zhuang
	zh: ["cn", "tw", "hk", "sg", "mo", "my", "id", "us", "th", "vn", "ph", "ca", "au", "gb", "mn"], // Chinese
	zu: ["za", "ls", "sz", "mw", "mz"], // Zulu
};

export const getCountriesForLanguage = (languageCode) => {
	if (!languageCode) return countryOptions;
	const codes = LANGUAGE_TO_COUNTRIES[languageCode];
	if (!codes || codes.length === 0) return countryOptions;
	return codes.map(buildCountryOption).sort(sortByLabel);
};

// Combined "Language (Country)" entries derived from LANGUAGE_TO_COUNTRIES.
// Each item has a stable `value` like "es-MX" so the picker can use it as the
// option key. The separate `language` and `country` codes are also returned
// so callers don't have to re-split the string. Sort: pinned languages
// (Spanish, French) first, then alphabetical by label.
const PINNED_LOCALE_LANGUAGES = ["es", "fr"];

const buildLocaleEntry = (language, country) => {
	const langOption = buildLanguageOption(language);
	const countryOption = buildCountryOption(country);
	return {
		value: `${language}-${country.toUpperCase()}`,
		language,
		country,
		label: `${langOption.label} (${countryOption.label})`,
		languageLabel: langOption.label,
		countryLabel: countryOption.label,
	};
};

export const localeOptions = (() => {
	const all = [];
	Object.entries(LANGUAGE_TO_COUNTRIES).forEach(([language, countries]) => {
		countries.forEach((country) => {
			all.push(buildLocaleEntry(language, country));
		});
	});
	const pinned = all.filter((l) => PINNED_LOCALE_LANGUAGES.includes(l.language));
	const others = all
		.filter((l) => !PINNED_LOCALE_LANGUAGES.includes(l.language))
		.sort(sortByLabel);
	pinned.sort(sortByLabel);
	return [...pinned, ...others];
})();
